import { Badge, Button, Card, Group, Select, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import type { QuestionDifficulty, QuestionStatus, QuestionType } from '@prepzio/shared';
import { useState } from 'react';

import { useContentSummary, useDrafts, useSkills } from '../hooks/use-content';
import { useEnumLabel, useT } from '../i18n';
import { errorMessage } from '../lib/ui';

const STATUSES: QuestionStatus[] = ['published', 'draft', 'rejected'];
const TYPES: QuestionType[] = ['free_text', 'mcq', 'coding'];
const DIFFICULTIES: QuestionDifficulty[] = ['easy', 'medium', 'hard'];

/**
 * Content overview — what's in the question bank and in which state. A
 * (skill × status) count matrix plus a filterable, browsable list. Read-only;
 * editing/approval lives in Review, deletion in Reset.
 */
export function ContentScreen() {
  const t = useT();
  const label = useEnumLabel();
  const summaryQuery = useContentSummary();
  // PRE-155: the authored taxonomy only. This table is question counts per
  // skill, and the catalog's 2,474 rows have none by construction — listing
  // them would be 2,474 rows of zero with the six that matter buried in it.
  const skillsQuery = useSkills({ origin: 'curated', limit: 100 });

  const [status, setStatus] = useState<QuestionStatus>('published');
  const [skillId, setSkillId] = useState('');
  const [type, setType] = useState<QuestionType | ''>('');
  const [difficulty, setDifficulty] = useState<QuestionDifficulty | ''>('');

  const listQuery = useDrafts({
    status,
    ...(skillId ? { skillId } : {}),
    ...(type ? { type } : {}),
    ...(difficulty ? { difficulty } : {}),
  });

  const summary = summaryQuery.data;
  const skills = skillsQuery.data?.items ?? [];
  const items = listQuery.data?.items ?? [];

  const countFor = (tId: string, s: QuestionStatus) =>
    summary?.counts.find((c) => c.skillId === tId && c.status === s)?.count ?? 0;

  if (summaryQuery.isError)
    return (
      <Text c="red" size="sm">
        {errorMessage(summaryQuery.error)}
      </Text>
    );

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t('content.intro')}
      </Text>

      {/* ── Summary matrix ── */}
      <Card withBorder padding="lg" radius="md">
        <Text fw={600} mb="xs">
          {t('content.bySkill')}
        </Text>
        {summary && (
          <Group gap="xs" mb="sm">
            <Text c="dimmed" size="sm">
              {t('content.totals')}
            </Text>
            <Badge variant="light" color="green">
              {t('content.publishedCount', { count: summary.totals.published })}
            </Badge>
            <Badge variant="light" color="gray">
              {t('content.draftCount', { count: summary.totals.draft })}
            </Badge>
            <Badge variant="light" color="yellow">
              {t('content.rejectedCount', { count: summary.totals.rejected })}
            </Badge>
          </Group>
        )}
        <Table.ScrollContainer minWidth={480}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('content.colSkill')}</Table.Th>
                <Table.Th>{t('content.colPublished')}</Table.Th>
                <Table.Th>{t('content.colDraft')}</Table.Th>
                <Table.Th>{t('content.colRejected')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(summary?.skills ?? []).map((skill) => (
                <Table.Tr key={skill.id}>
                  <Table.Td>
                    {skill.title}{' '}
                    <Text component="span" c="dimmed" size="sm">
                      ({skill.slug})
                    </Text>
                  </Table.Td>
                  <Table.Td>{countFor(skill.id, 'published')}</Table.Td>
                  <Table.Td>{countFor(skill.id, 'draft')}</Table.Td>
                  <Table.Td>{countFor(skill.id, 'rejected')}</Table.Td>
                </Table.Tr>
              ))}
              {summary && summary.skills.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" size="sm">
                      {t('content.noSkills')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {/* ── Browse ── */}
      <Card withBorder padding="lg" radius="md">
        <Text fw={600} mb="xs">
          {t('content.browse')}
        </Text>
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
          <Select
            label={t('common.status')}
            data={STATUSES.map((s) => ({ value: s, label: label('status', s) }))}
            value={status}
            onChange={(v) => setStatus((v as QuestionStatus) ?? 'published')}
            allowDeselect={false}
          />
          <Select
            label={t('common.skill')}
            data={[
              { value: '', label: t('content.allSkills') },
              ...skills.map((s) => ({ value: s.id, label: s.title })),
            ]}
            value={skillId}
            onChange={(v) => setSkillId(v ?? '')}
            allowDeselect={false}
            searchable
          />
          <Select
            label={t('content.type')}
            data={[
              { value: '', label: t('common.all') },
              ...TYPES.map((v) => ({ value: v, label: label('type', v) })),
            ]}
            value={type}
            onChange={(v) => setType((v as QuestionType | '') ?? '')}
            allowDeselect={false}
          />
          <Select
            label={t('content.difficulty')}
            data={[
              { value: '', label: t('common.all') },
              ...DIFFICULTIES.map((d) => ({ value: d, label: label('difficulty', d) })),
            ]}
            value={difficulty}
            onChange={(v) => setDifficulty((v as QuestionDifficulty | '') ?? '')}
            allowDeselect={false}
          />
          <Group align="flex-end">
            <Button variant="default" onClick={() => void listQuery.refetch()} fullWidth>
              {t('common.refresh')}
            </Button>
          </Group>
        </SimpleGrid>

        {listQuery.isLoading && (
          <Text c="dimmed" size="sm" mt="sm">
            {t('common.loading')}
          </Text>
        )}
        {listQuery.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(listQuery.error)}
          </Text>
        )}
        {!listQuery.isLoading && items.length === 0 && (
          <Text c="dimmed" size="sm" mt="sm">
            {t('content.nothingHere')}
          </Text>
        )}

        <Stack gap="sm" mt="sm">
          {items.map((q) => (
            <Card key={q.id} withBorder padding="md" radius="sm">
              <Group gap="xs" mb="xs">
                <Badge variant="light" color="gray">
                  {label('type', q.type)}
                </Badge>
                <Badge variant="light" color="gray">
                  {label('difficulty', q.difficulty)}
                </Badge>
                <Badge
                  variant="light"
                  color={
                    q.status === 'published' ? 'green' : q.status === 'rejected' ? 'red' : 'gray'
                  }
                >
                  {label('status', q.status)}
                </Badge>
                {q.judgeScore != null && (
                  <Badge variant="light" color={q.judgeScore >= 0.6 ? 'green' : 'yellow'}>
                    {t('question.judge', { score: q.judgeScore.toFixed(2) })}
                  </Badge>
                )}
                {q.duplicateOfId != null && (
                  <Badge variant="light" color="yellow">
                    {t('content.nearDup')}
                  </Badge>
                )}
              </Group>
              <Text size="sm">{q.prompt}</Text>
              {q.expectedConcepts.length > 0 && (
                <Text c="dimmed" size="sm" mt={4}>
                  {t('content.expected', { concepts: q.expectedConcepts.join(', ') })}
                </Text>
              )}
            </Card>
          ))}
        </Stack>
        {items.length >= 50 && (
          <Text c="dimmed" size="sm" mt="sm">
            {t('content.first50')}
          </Text>
        )}
      </Card>
    </Stack>
  );
}
